/**
 * Product-level citation policy for simulation results (owner decision,
 * 6 Oct 2026, release 2.1 — option (a) "product-level citation policy").
 *
 * SINGLE SOURCE OF TRUTH for whether the product presents a simulation result
 * as citable. Every surface (editor chips/docks/History, report exhibits and
 * promotion, study gates, exports, capability registry) asks this module and
 * nothing else.
 *
 * Two layers, deliberately separate:
 *
 *  1. Runtime self-certification (frozen). The solver's wire fields
 *     `citable: false`, `capability_status: "preview"`,
 *     `artifact_citation_mode: "none"`, its disclaimer text and the
 *     `preview_non_citable` limitation code sit INSIDE the signed result bytes,
 *     the generated SDK/OpenAPI and the stored run artifacts. They mean "the
 *     solver does not certify its own output" and stay byte-identical (no
 *     solver, fixture, manifest or release-identity change). This module never
 *     reads them, and no product surface may present them as the citation
 *     decision. Changing the API to carry the product decision is Phase 2
 *     (alongside P2-3).
 *
 *  2. Product citation (this module). A result is citable exactly when the
 *     facts established by fail-closed SERVER verification hold: it is an
 *     immutable, tenant-owned persisted record; the server verified it; and it
 *     carries a verified release identity. The policy only reads those facts;
 *     it never replaces, relaxes or re-derives verification. Anything unsaved,
 *     unverified or unbound is not citable and says why.
 *
 * No source/validation line is attached to cited results (owner: no). The
 * scientific sign-off (Phase 2 item P2-0) remains the next step and is not a
 * precondition for citation under this policy.
 */

/** Stable identifier persisted with new exhibits/exports and listed in the capability registry. */
export const PRODUCT_CITATION_POLICY_ID = "release-citable-verified" as const;
export type ProductCitationPolicyId = typeof PRODUCT_CITATION_POLICY_ID;

/**
 * Facts the server established, read from the verified record — never from
 * the solver's wire flags and never inferred in the browser.
 */
export interface ProductCitationFacts {
  /** An immutable, tenant-owned persisted record exists (project run/study). Session/scratch results: false. */
  readonly persistedOwned: boolean;
  /** The record passed fail-closed server verification (`serverVerified === true` on the record). */
  readonly serverVerified: boolean;
  /** The record carries a verified release identity (release manifest / image / platform tuple). */
  readonly releaseBound: boolean;
}

export type ProductCitationReason =
  | "unsaved-session-result"
  | "not-server-verified"
  | "release-not-bound";

export type ProductCitationDisposition =
  | {
      readonly citable: true;
      readonly policyId: ProductCitationPolicyId;
      readonly basis: "server-verified-owned-release-bound";
    }
  | {
      readonly citable: false;
      readonly policyId: ProductCitationPolicyId;
      readonly reason: ProductCitationReason;
    };

const CITABLE: ProductCitationDisposition = Object.freeze({
  citable: true,
  policyId: PRODUCT_CITATION_POLICY_ID,
  basis: "server-verified-owned-release-bound",
});

function notCitable(reason: ProductCitationReason): ProductCitationDisposition {
  return Object.freeze({ citable: false, policyId: PRODUCT_CITATION_POLICY_ID, reason });
}

/**
 * The policy. Fails closed: each fact must be exactly `true`; the first
 * missing fact (in the order a user can act on it) is the reason.
 */
export function productCitationDisposition(facts: ProductCitationFacts): ProductCitationDisposition {
  if (facts.persistedOwned !== true) return notCitable("unsaved-session-result");
  if (facts.serverVerified !== true) return notCitable("not-server-verified");
  if (facts.releaseBound !== true) return notCitable("release-not-bound");
  return CITABLE;
}

/**
 * Disposition of a surface built from several records (a run comparison): citable only
 * when every record is; otherwise the first record's failing reason, in input order.
 * No records: an unsaved session result.
 */
export function combinedProductCitationDisposition(
  dispositions: readonly ProductCitationDisposition[],
): ProductCitationDisposition {
  if (dispositions.length === 0) return notCitable("unsaved-session-result");
  for (const disposition of dispositions) {
    if (!disposition.citable) return disposition;
  }
  return CITABLE;
}

/** Disposition for a result that only exists in the session (scratch Solve, unsaved draft). */
export const UNSAVED_SESSION_RESULT_CITATION: ProductCitationDisposition = notCitable("unsaved-session-result");

/**
 * Minimal view of a persisted, tenant-owned record the browser received through
 * a fail-closed verifying client or the server reads back from its own store:
 * project runs (steady, transient, restart, scheduled and adaptive windows,
 * graph-v3 artifacts, sparse-Water runs), project studies, and site-level
 * steady/transient rows. Only `serverVerified` and the release tuple the server
 * wrote beside it are read; nothing else on the record is, and never the
 * solver's self-certification flags.
 */
export interface PersistedCitationRecord {
  readonly serverVerified?: unknown;
  readonly releaseIdentity?: unknown;
  readonly serverVerification?: unknown;
}

/** The verified release tuple every stored schema carries (extra fields such as `propertyPackageVersions` are ignored). */
export interface VerifiedReleaseIdentityTuple {
  readonly releaseManifestDigest: string;
  readonly solverImageDigest: string;
  readonly platformReleaseSha: string;
  readonly componentManifestDigest: string;
  readonly graphContractVersion: number;
}

const RELEASE_IDENTITY_DIGEST_FIELDS = [
  "releaseManifestDigest",
  "solverImageDigest",
  "platformReleaseSha",
  "componentManifestDigest",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * THE release-bound rule (one definition for every record shape): a release
 * tuple counts as verified only when it is a plain object whose four digests
 * (`releaseManifestDigest`, `solverImageDigest`, `platformReleaseSha`,
 * `componentManifestDigest`) are non-empty strings and whose
 * `graphContractVersion` is a positive integer. A partial tuple, `{}`, `null`,
 * an array or a non-string digest is NOT bound. Digest formats are the
 * verifying reader's job (it already rejected malformed tuples before the
 * record reached this module); the policy only reads the facts.
 */
export function verifiedReleaseIdentityTuple(value: unknown): VerifiedReleaseIdentityTuple | undefined {
  if (!isRecord(value)) return undefined;
  for (const field of RELEASE_IDENTITY_DIGEST_FIELDS) {
    const digest = value[field];
    if (typeof digest !== "string" || digest.length === 0) return undefined;
  }
  const version = value.graphContractVersion;
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) return undefined;
  return value as unknown as VerifiedReleaseIdentityTuple;
}

/**
 * The record's verified release tuple, wherever the stored schema keeps it;
 * `undefined` when unbound. Three locations, one rule
 * ({@link verifiedReleaseIdentityTuple}), checked in this order:
 *
 *  1. `record.releaseIdentity` — a flattened view of a persisted record;
 *  2. `record.serverVerification.releaseIdentity` — project runs, restarts,
 *     scheduled/adaptive windows, graph-v3 artifacts, sparse runs, project
 *     studies (nested tuple written by the server);
 *  3. flat on `record.serverVerification` — site-level `eo-private-proxy-v2`
 *     steady rows and `eo-private-integrate-v2` transient rows (the server
 *     stamps the five fields beside the other verification fields). For this
 *     shape the five fields are copied into a fresh tuple so callers never see
 *     the rest of the verification block.
 *
 * Shapes 1 and 2 return the stored object itself (it is the tuple).
 */
export function persistedRecordReleaseIdentity(record: PersistedCitationRecord): VerifiedReleaseIdentityTuple | undefined {
  const flattened = verifiedReleaseIdentityTuple(record.releaseIdentity);
  if (flattened !== undefined) return flattened;
  const verification = record.serverVerification;
  if (!isRecord(verification)) return undefined;
  const nested = verifiedReleaseIdentityTuple(verification.releaseIdentity);
  if (nested !== undefined) return nested;
  const flat = verifiedReleaseIdentityTuple(verification);
  return flat === undefined
    ? undefined
    : {
        releaseManifestDigest: flat.releaseManifestDigest,
        solverImageDigest: flat.solverImageDigest,
        platformReleaseSha: flat.platformReleaseSha,
        componentManifestDigest: flat.componentManifestDigest,
        graphContractVersion: flat.graphContractVersion,
      };
}

/**
 * THE fact reader for a persisted record. `persistedOwned` is true by
 * construction (the caller holds a stored, tenant-owned record); the other two
 * facts come from `serverVerified === true` and
 * {@link persistedRecordReleaseIdentity}.
 */
export function persistedRecordCitationFacts(record: PersistedCitationRecord): ProductCitationFacts {
  return {
    persistedOwned: true,
    serverVerified: record.serverVerified === true,
    releaseBound: persistedRecordReleaseIdentity(record) !== undefined,
  };
}

/** Disposition for an optional persisted record; no record means the result only exists in the session. */
export function persistedRecordCitation(
  record: PersistedCitationRecord | object | null | undefined,
): ProductCitationDisposition {
  // Any stored record shape is accepted; only the three fact fields are read.
  return record ? productCitationDisposition(persistedRecordCitationFacts(record as PersistedCitationRecord)) : UNSAVED_SESSION_RESULT_CITATION;
}

/**
 * Disposition for a verified graph-v3 project artifact handle
 * (`Verified…ProjectArtifact` / tank `projectArtifact`). These opaque handles are
 * minted only by the authenticated schema-14/15 parsers, which fail closed unless
 * the record carries `serverVerified === true` and a parsed verified
 * `serverVerification.releaseIdentity`; the handle's existence is therefore the
 * server's attestation of all three facts. Absent handle: session-only result.
 * This is the only other record shape; it carries no readable fields.
 */
export function verifiedProjectArtifactCitation(artifact: object | false | null | undefined): ProductCitationDisposition {
  return artifact
    ? productCitationDisposition({ persistedOwned: true, serverVerified: true, releaseBound: true })
    : UNSAVED_SESSION_RESULT_CITATION;
}

/**
 * Top-level `citation` block carried by every NEW export format id. Historical
 * export formats are never rewritten; they simply lack this block.
 */
export type ProductCitationExportBlock =
  | {
      readonly policyId: ProductCitationPolicyId;
      readonly citable: true;
      readonly basis: "server-verified-owned-release-bound";
      readonly releaseIdentity: VerifiedReleaseIdentityTuple;
    }
  | {
      readonly policyId: ProductCitationPolicyId;
      readonly citable: false;
      readonly reason: ProductCitationReason;
    };

/** Export block for a persisted record; the release tuple is copied only when the record is citable. */
export function productCitationExportBlock(record: PersistedCitationRecord): ProductCitationExportBlock {
  const disposition = persistedRecordCitation(record);
  return disposition.citable
    ? {
        policyId: disposition.policyId,
        citable: true,
        basis: disposition.basis,
        releaseIdentity: structuredClone(persistedRecordReleaseIdentity(record) as VerifiedReleaseIdentityTuple),
      }
    : { policyId: disposition.policyId, citable: false, reason: disposition.reason };
}

/** Short, user-facing status text. No "preview" watermark; no validation note (owner: no). */
export const PRODUCT_CITATION_LABEL: Readonly<Record<"citable" | ProductCitationReason, string>> = Object.freeze({
  citable: "Server-verified · citable",
  "unsaved-session-result": "Not saved · run a saved revision to cite",
  "not-server-verified": "Not server-verified · not citable",
  "release-not-bound": "No verified release · not citable",
});

export function productCitationLabel(disposition: ProductCitationDisposition): string {
  return disposition.citable ? PRODUCT_CITATION_LABEL.citable : PRODUCT_CITATION_LABEL[disposition.reason];
}

/**
 * One sentence for file headers and export boundaries (CSV comment lines,
 * workbook "Read me" rows, JSON `boundary.statement`). Names the policy so a
 * reader can find the rule; no preview watermark, no validation note.
 */
export function productCitationStatement(disposition: ProductCitationDisposition): string {
  return disposition.citable
    ? `Citable under product citation policy ${disposition.policyId}: saved, server-verified, release-bound record.`
    : `Not citable under product citation policy ${disposition.policyId}: ${PRODUCT_CITATION_LABEL[disposition.reason]}.`;
}

/** Compact badge text for dense docks/cards (same disposition as {@link productCitationLabel}). */
export const PRODUCT_CITATION_BADGE: Readonly<Record<"citable" | ProductCitationReason, string>> = Object.freeze({
  citable: "Citable",
  "unsaved-session-result": "Not saved",
  "not-server-verified": "Not server-verified",
  "release-not-bound": "No verified release",
});

export function productCitationBadge(disposition: ProductCitationDisposition): string {
  return disposition.citable ? PRODUCT_CITATION_BADGE.citable : PRODUCT_CITATION_BADGE[disposition.reason];
}

/** One-sentence explanation of a disposition for banners and tooltips. */
export const PRODUCT_CITATION_EXPLANATION: Readonly<Record<"citable" | ProductCitationReason, string>> = Object.freeze({
  citable: "This immutable record was verified by the server and bound to a verified release; it may be cited as it is.",
  "unsaved-session-result": "This result exists only in this session. Run it as a saved revision to cite it.",
  "not-server-verified": "The server has not verified this record, so it is not citable.",
  "release-not-bound": "This server-verified record carries no verified release identity, so it is not citable.",
});

export function productCitationExplanation(disposition: ProductCitationDisposition): string {
  return disposition.citable ? PRODUCT_CITATION_EXPLANATION.citable : PRODUCT_CITATION_EXPLANATION[disposition.reason];
}

/** Value for `data-citable` attributes on product surfaces. */
export function productCitationDataAttribute(disposition: ProductCitationDisposition): "true" | "false" {
  return disposition.citable ? "true" : "false";
}

/**
 * Spreadable `data-*` attributes for a product surface: `data-citable`,
 * `data-citation-policy`, and `data-citation-reason` (only when not citable).
 */
export function productCitationDataAttributes(disposition: ProductCitationDisposition): {
  readonly "data-citable": "true" | "false";
  readonly "data-citation-policy": ProductCitationPolicyId;
  readonly "data-citation-reason"?: ProductCitationReason;
} {
  return disposition.citable
    ? { "data-citable": "true", "data-citation-policy": disposition.policyId }
    : { "data-citable": "false", "data-citation-policy": disposition.policyId, "data-citation-reason": disposition.reason };
}
